import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SeoMeta from "../SeoMeta";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";
import { createAdminPurchase } from "../../apis/AdminPurchasesApis";
import { fetchAllCourses, fetchCourseById } from "../../apis/CourseApis";
import { getAllUsersAdmin } from "../../apis/UserApis";

const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "bg-green-100 text-green-800" },
  { value: "refunded", label: "Refunded", color: "bg-yellow-100 text-yellow-800" },
  { value: "expired", label: "Expired", color: "bg-red-100 text-red-800" },
];

export default function CreatePurchase() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const initialCourseId = useMemo(() => searchParams.get("courseId") || "", [searchParams]);

  const [courseId, setCourseId] = useState(initialCourseId);
  const [status, setStatus] = useState("active");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdPurchase, setCreatedPurchase] = useState(null);

  // Courses state (for dropdown when no courseId is provided)
  const [courses, setCourses] = useState([]);
  const [isCoursesLoading, setIsCoursesLoading] = useState(false);
  const [course, setCourse] = useState(null);
  const [isCourseLoading, setIsCourseLoading] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Load all courses if no courseId is provided
  useEffect(() => {
    if (initialCourseId) return; // Skip if courseId is already provided
    
    let isMounted = true;
    const loadCourses = async () => {
      setIsCoursesLoading(true);
      try {
        const data = await fetchAllCourses();
        const list = Array.isArray(data) ? data : Array.isArray(data?.courses) ? data.courses : [];
        if (isMounted) setCourses(list);
      } catch (err) {
        console.error("Failed to load courses:", err);
        if (isMounted) setCourses([]);
      } finally {
        if (isMounted) setIsCoursesLoading(false);
      }
    };
    loadCourses();
    return () => { isMounted = false; };
  }, [initialCourseId]);

  // Load single course details when courseId changes
  useEffect(() => {
    let isMounted = true;
    const loadCourse = async () => {
      if (!courseId) {
        setCourse(null);
        return;
      }
      setIsCourseLoading(true);
      try {
        const data = await fetchCourseById(courseId);
        if (isMounted) setCourse(data);
      } catch (err) {
        console.error("Failed to load course:", err);
        if (isMounted) setCourse(null);
      } finally {
        if (isMounted) setIsCourseLoading(false);
      }
    };
    loadCourse();
    return () => { isMounted = false; };
  }, [courseId]);

  // Load users for admin search/picker.
  useEffect(() => {
    let isMounted = true;
    const loadUsers = async () => {
      if (!user?.token) return;
      setIsUsersLoading(true);
      try {
        const data = await getAllUsersAdmin(user.token);
        const list = Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : [];
        if (isMounted) setUsers(list);
      } catch (err) {
        console.error("Failed to load users:", err);
        if (isMounted) setUsers([]);
      } finally {
        if (isMounted) setIsUsersLoading(false);
      }
    };
    loadUsers();
    return () => { isMounted = false; };
  }, [user?.token]);

  const formatUserLabel = (u) => {
    const firstName = u?.firstName || "";
    const lastName = u?.lastName || "";
    const email = u?.email || "";
    const name = `${firstName} ${lastName}`.trim();
    if (name && email) return `${name} (${email})`;
    return name || email || "Unknown user";
  };

  const getUserId = (u) => String(u?.id || u?._id || "");
  const getCourseId = (c) => String(c?.id || c?._id || "");

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    const source = Array.isArray(users) ? users : [];
    if (!q) return source.slice(0, 50);
    return source
      .filter((u) => {
        const hay = `${u?.firstName || ""} ${u?.lastName || ""} ${u?.email || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [users, userQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.token) {
      errorToast("You must be logged in as admin");
      return;
    }

    if (!courseId?.trim()) {
      errorToast("Please select a course");
      return;
    }

    const selectedUserId = getUserId(selectedUser);
    if (!selectedUserId) {
      errorToast("Please select a user");
      return;
    }

    setIsSubmitting(true);
    setCreatedPurchase(null);

    try {
      const data = await createAdminPurchase({
        userId: selectedUserId,
        courseId: courseId.trim(),
        status,
        token: user.token,
      });

      setCreatedPurchase(data);
      successToast("Access granted", "Purchase created successfully");
    } catch (err) {
      console.error("Failed to create purchase:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create purchase";
      errorToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setUserQuery("");
    setCreatedPurchase(null);
    if (!initialCourseId) {
      setCourseId("");
      setCourse(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoMeta
        title="Create Purchase | Admin - Code Arena"
        description="Grant course access to a user by creating a purchase record."
        url="https://www.code-arena.in/admin/purchases/new"
        image="/logo192.png"
      />

      {/* Header */}
      <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} text-white`}>
        <div className="ca-container py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line text-xl"></i>
                </div>
                <h1 className="text-2xl font-bold">Grant Course Access</h1>
              </div>
              <p className="text-white/80">
                Create a purchase record to give a user access to a course
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-line"></i> Back
            </button>
          </div>
        </div>
      </div>

      <div className="ca-container py-8 max-w-3xl">
        <div className="grid gap-6">
          {/* Main Form Card */}
          <div className="ca-card overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-file-list-3-line text-green-600"></i>
                Purchase Details
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-book-open-line mr-1 text-green-600"></i>
                  Select Course
                </label>

                {initialCourseId ? (
                  // Course is pre-selected from URL param
                  <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white">
                        <i className="ri-book-2-line text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {isCourseLoading ? (
                            <span className="text-gray-500">Loading course...</span>
                          ) : (
                            course?.title || "Course"
                          )}
                        </div>
                        <div className="text-xs text-gray-500">ID: {courseId}</div>
                      </div>
                      <Link
                        to={`/courses/${courseId}`}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        View <i className="ri-external-link-line"></i>
                      </Link>
                    </div>
                  </div>
                ) : (
                  // Dropdown to select course
                  <div>
                    {isCoursesLoading ? (
                      <div className="ca-input flex items-center text-gray-500">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Loading courses...
                      </div>
                    ) : (
                      <select
                        className="ca-input w-full"
                        value={courseId}
                        onChange={(e) => setCourseId(e.target.value)}
                        required
                      >
                        <option value="">-- Select a course --</option>
                        {courses.map((c) => (
                          <option key={getCourseId(c)} value={getCourseId(c)}>
                            {c.title || c.name || getCourseId(c)}
                          </option>
                        ))}
                      </select>
                    )}
                    {course && (
                      <div className="mt-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            {course.title}
                          </span>
                          <Link
                            to={`/courses/${courseId}`}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            View course <i className="ri-external-link-line"></i>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-user-search-line mr-1 text-blue-600"></i>
                  Select User
                </label>

                <div className="relative">
                  <input
                    className="ca-input w-full pl-10"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Search by name or email..."
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                </div>

                <div className="mt-2 border border-gray-200 rounded-xl max-h-64 overflow-auto bg-white">
                  {isUsersLoading ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Loading users...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No users found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredUsers.map((u) => {
                        const id = getUserId(u);
                        const isSelected = Boolean(selectedUser && getUserId(selectedUser) === id);
                        return (
                          <button
                            key={id || `${u?.email}-${u?.firstName}`}
                            type="button"
                            onClick={() => setSelectedUser(u)}
                            className={`w-full text-left px-4 py-3 transition-colors ${
                              isSelected
                                ? "bg-blue-50 border-l-4 border-blue-500"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                                {u?.firstName?.charAt(0) || "U"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {u?.firstName} {u?.lastName}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {u?.email}
                                </div>
                              </div>
                              {isSelected && (
                                <i className="ri-checkbox-circle-fill text-blue-600 text-lg"></i>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedUser && (
                  <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className="ri-user-follow-line text-blue-600"></i>
                        <span className="text-sm font-medium text-blue-900">
                          {formatUserLabel(selectedUser)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedUser(null)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-flag-line mr-1 text-purple-600"></i>
                  Purchase Status
                </label>
                <div className="flex gap-3">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatus(opt.value)}
                      className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${
                        status === opt.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${opt.color}`}
                        >
                          {opt.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting || !courseId || !getUserId(selectedUser)}
                  className="ca-btn-primary px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Grant Access
                    </>
                  )}
                </button>
                <Link to="/admin" className="ca-btn-secondary px-4 py-2.5">
                  Cancel
                </Link>
              </div>
            </form>
          </div>

          {/* Success Card */}
          {createdPurchase && (
            <div className="ca-card overflow-hidden border-2 border-green-200">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-4">
                <h2 className="font-semibold text-white flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  Access Granted Successfully!
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Purchase ID</span>
                    <div className="font-mono text-gray-900">
                      {createdPurchase.id || createdPurchase._id}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Status</span>
                    <div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        {createdPurchase.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">User ID</span>
                    <div className="font-mono text-gray-900 truncate">
                      {createdPurchase.userId}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Course ID</span>
                    <div className="font-mono text-gray-900 truncate">
                      {createdPurchase.courseId}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="ca-btn-primary px-4 py-2 text-sm"
                  >
                    <i className="ri-add-line mr-1"></i> Create Another
                  </button>
                  <Link
                    to={`/courses/${createdPurchase.courseId}`}
                    className="ca-btn-secondary px-4 py-2 text-sm"
                  >
                    View Course
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
